import { createFileRoute } from '@tanstack/react-router'
import { useEffect } from 'react'

import { useCreateSupplier } from '@/api/api-mutations'
import { useGetDashboardFeatures } from '@/api/react-query'
import { Button } from '@/components/base/button'
import { Drawer } from '@/components/base/drawer'
import { Heading } from '@/components/base/heading'
import { toast, useCreatedToast } from '@/components/base/toast'
import { FormContainer, FormFieldGroup, useAppForm } from '@/components/form/form'
import { RouteModal, RouteModalFormBlocker, useRouteModal } from '@/components/route-modal'
import { useI18n } from '@/i18n/i18n-react'

import { getCurrencyOptions } from '../-currency-utils'

export const Route = createFileRoute('/_authenticated/_general/inventory/suppliers/create')({
	component: CreateSupplierRoute,
	staticData: {
		crumb: ({ context: { L } }) => L.Inventory.CreateSupplier()
	}
})

function CreateSupplierRoute() {
	return (
		<RouteModal asDrawer prev='..'>
			<CreateSupplierForm />
		</RouteModal>
	)
}

function CreateSupplierForm() {
	const { L, locale } = useI18n()
	const { handleSuccess } = useRouteModal()
	const createdToast = useCreatedToast()
	const createSupplier = useCreateSupplier(true)
	const dashboardFeatures = useGetDashboardFeatures()
	const tenantDefaultCurrencyCode = dashboardFeatures.data?.reportingCurrencyCode

	const form = useAppForm({
		defaultValues: {
			name: '',
			email: '',
			currencyCode: ''
		},
		onSubmit: async ({ value }) => {
			if (!value.currencyCode) {
				toast.error(L.General.Required())
				return
			}
			const result = await createSupplier.mutateAsync({
				body: {
					name: value.name,
					email: value.email || undefined,
					currencyCode: value.currencyCode
				}
			})
			createdToast(L.Inventory.CreateSupplier(), {
				to: '/inventory/suppliers/$supplierUuid/edit',
				params: { supplierUuid: result.response.uuid }
			})
			handleSuccess()
		}
	})

	// Fold the async default currency into the form baseline via `reset` so a
	// freshly-opened form isn't considered changed (which would trigger a false
	// "Unsaved changes" prompt on cancel). Only do this while the form is still
	// untouched: if the default arrives after the user has started editing,
	// resetting would clobber their input and rebaseline their edits (hiding them
	// from the unsaved-changes blocker).
	useEffect(() => {
		if (!tenantDefaultCurrencyCode) return
		if (!form.state.isDefaultValue) return
		form.reset({ ...form.state.values, currencyCode: tenantDefaultCurrencyCode })
	}, [form, tenantDefaultCurrencyCode])

	return (
		<form.AppForm>
			<RouteModalFormBlocker form={form as any} />
			<Drawer.Header>
				<Drawer.Title asChild>
					<Heading>{L.Inventory.CreateSupplier()}</Heading>
				</Drawer.Title>
			</Drawer.Header>
			<Drawer.Body className='flex-1 overflow-auto'>
				<FormContainer>
					<FormFieldGroup>
						<form.AppField
							name='name'
							validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
							children={field => <field.TextField label={L.Inventory.Column.Name()} autoFocus />}
						/>
						<form.AppField
							name='email'
							children={field => <field.TextField label={L.Inventory.Column.Email()} optional />}
						/>
						<form.AppField
							name='currencyCode'
							validators={{ onChange: ({ value }) => (!value ? L.General.Required() : undefined) }}
							children={field => (
								<field.SelectField
									label={L.Inventory.Column.Currency()}
									options={getCurrencyOptions(locale)}
								/>
							)}
						/>
					</FormFieldGroup>
				</FormContainer>
			</Drawer.Body>
			<Drawer.Footer>
				<div className='flex items-center justify-end gap-x-2'>
					<Drawer.Close asChild>
						<Button variant='secondary' size='small' type='button'>
							{L.General.Cancel()}
						</Button>
					</Drawer.Close>
					<form.CreateButton />
				</div>
			</Drawer.Footer>
		</form.AppForm>
	)
}
